import { useSelector } from "react-redux";
import { RootState } from "../../store";
import React, { useEffect, useState } from "react";

import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";

import Errors, {
  extractArrayErrors,
  getFieldErrors,
} from "../../models/errors";
import {
  INVOICE_SALE_TYPE_CASH,
  INVOICE_SALE_TYPE_CASH_LABEL,
  INVOICE_SALE_TYPE_CREDIT,
  INVOICE_SALE_TYPE_CREDIT_LABEL,
  ReceivedInvoiceItem,
  newReceivedInvoice,
  newReceivedInvoiceItem,
  ReceivedInvoice,
} from "../../models/received-invoice";

import { useHistory } from "react-router-dom";
import { errorAlert, successAlert } from "../utils/messages";

import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CCol,
  CFormGroup,
  CInput,
  CInputRadio,
  CLabel,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";

import { FieldErrors } from "../form/FieldErrors";

import Loading from "../indicators/Loading";

import { CURRENCY_CHOICES, PYG } from "../../currency/available-currencies";
import CurrencyField from "../currencies/CurrencyField";
import ReceivedInvoiceItemForm from "./ReceivedInvoiceItemForm";
import { Spinner } from "react-bootstrap";
import { getRoutePath } from "../../routes";

import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";

import PYGField from "../currencies/PYGField";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";

import { emptyValueOnUndefined } from "../../utils/fields";
import Supplier from "../../models/supplier";
import SupplierSelect from "../supplier/SupplierSelect";

interface ReceivedInvoiceFormProps {
  readonly?: boolean;
}

const ReceivedInvoiceForm: React.FC<ReceivedInvoiceFormProps> = ({
  readonly,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const [loadError, setLoadError] = useState(false);
  const [loadNotFound, setLoadNotFound] = useState(false);

  const [editingInvoice, setEditingInvoice] = useState<ReceivedInvoice | null>(
    newReceivedInvoice(taxPayer.id)
  );

  const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(undefined);
  const [stampDate, setStampDate] = useState<Date | undefined>(undefined);

  const [, setSaleType] = useState<string | undefined>(undefined);
  const [currency, setCurrency] = useState(PYG);
  const [pygExchangeRate, setPygExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [amount10Percent, setAmount10Percent] = useState<number | undefined>(
    undefined
  );
  const [amount5Percent, setAmount5Percent] = useState<number | undefined>(
    undefined
  );
  const [amountExempt, setAmountExempt] = useState<number | undefined>(
    undefined
  );
  const [iva10Percent, setIva10Percent] = useState<number | undefined>(
    undefined
  );
  const [iva5Percent, setIva5Percent] = useState<number | undefined>(undefined);

  const [items, setItems] = useState<ReceivedInvoiceItem[]>([]);

  const [selectedSupplier, setSelectedSupplier] = useState<
    Supplier | undefined
  >(undefined);

  const history = useHistory();

  const getEditingInvoiceId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const editingInvoiceId = urlParams.get("id");

    if (editingInvoiceId !== null) {
      if (!isNaN(parseInt(editingInvoiceId))) {
        return parseInt(editingInvoiceId);
      }
    }

    return undefined;
  };

  const onSupplierChange = (newSupplier: Supplier | null) => {
    setSelectedSupplier(newSupplier !== null ? newSupplier : undefined);
    setEditingInvoice({
      ...editingInvoice,
      supplierId: newSupplier !== null ? newSupplier.id : undefined,
      supplierSocialName:
        newSupplier !== null
          ? newSupplier.legalName || newSupplier.fantasyName
          : undefined,
      supplierFantasyName:
        newSupplier !== null ? newSupplier.fantasyName : undefined,
      supplierRuc: newSupplier !== null ? newSupplier.ruc : undefined,
    });
  };

  const onSupplierSocialNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditingInvoice({
      ...editingInvoice,
      supplierSocialName: e.target.value,
    });
  };

  const onSupplierFantasyNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditingInvoice({
      ...editingInvoice,
      supplierFantasyName: e.target.value,
    });
  };

  const onSupplierRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingInvoice({
      ...editingInvoice,
      supplierRuc: e.target.value,
    });
  };

  const onStampIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingInvoice({
      ...editingInvoice,
      stampIdentifier: e.target.value,
    });
  };

  const onStampBeginDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;

    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    const stampDate = new Date((value as moment.Moment).toISOString());
    setStampDate(stampDate);

    setEditingInvoice({
      ...editingInvoice,
      stampBeginDate: changeTZToUtc(stampDate).toISOString(),
    });
  };

  const onInvoiceDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;

    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    const invoiceDate = new Date((value as moment.Moment).toISOString());
    setInvoiceDate(invoiceDate);

    setEditingInvoice({
      ...editingInvoice,
      invoiceDate: changeTZToUtc(invoiceDate).toISOString(),
    });
  };

  const onEstablishmentCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditingInvoice({
      ...editingInvoice,
      establishmentCode: e.target.value,
    });
  };

  const onDispatchPointIdentifierChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditingInvoice({
      ...editingInvoice,
      dispatchPointIdentifier: e.target.value,
    });
  };

  const onInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingInvoice({
      ...editingInvoice,
      invoiceNumber: e.target.value,
    });
  };

  const onSaleTypeChange = (newSaleType: string) => {
    setEditingInvoice({
      ...editingInvoice,
      saleType: newSaleType,
    });
    setSaleType(newSaleType);
  };

  const onCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingInvoice({
      ...editingInvoice,
      currency: e.target.value,
    });
    setCurrency(e.target.value);
  };

  const onPygExchangeRateChange = (newExchangeRate?: number) => {
    setEditingInvoice({
      ...editingInvoice,
      pygExchangeRate: newExchangeRate,
    });
    setPygExchangeRate(newExchangeRate);
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingInvoice({
      ...editingInvoice,
      obs: e.target.value,
    });
  };

  const onSave = async () => {
    if (editingInvoice === null) {
      return;
    }

    setSubmitting(true);

    let requestPromise: Promise<ItemRequestStatus<ReceivedInvoice>>;
    let successMessage = "Comprobante agregado exitósamente";

    const invoice = editingInvoice;
    invoice.clientRuc = taxPayer.ruc;
    invoice.clientSocialName = taxPayer.socialName;
    invoice.clientFantasyName = taxPayer.fantasyName;
    if (invoice.creationType === undefined) {
      invoice.creationType = "manual";
    }

    if (invoice.currency === undefined) {
      invoice.currency = PYG;
    }

    if (invoice.id === undefined) {
      requestPromise = createItem<ReceivedInvoice>(
        "/received_invoices/create/",
        invoice
      );
    } else {
      requestPromise = updateItem<ReceivedInvoice>(
        `/received_invoices/${invoice.id}/`,
        invoice
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }
      console.log(requestStatus);

      let message = "Ha ocurrido un error!!";

      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
    } else {
      successAlert(successMessage);
      history.push(getRoutePath("receivedInvoices"));
    }
  };

  const loadInitialInvoiceById = async (): Promise<ReceivedInvoice | null> => {
    const editingInvoiceId = getEditingInvoiceId();

    if (editingInvoiceId === undefined) {
      return null;
    }

    let requestStatus = await getItem<ReceivedInvoice>(
      `/received_invoices/${editingInvoiceId}/`
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.status === NOT_FOUND) {
        setLoadNotFound(true);
      } else {
        setLoadError(true);
      }

      return null;
    }

    if (requestStatus.data === undefined || requestStatus.data === null) {
      setLoadError(true);
      return null;
    }

    return requestStatus.data!;
  };

  const loadInitialData = async () => {
    setLoading(true);
    const invoice = await loadInitialInvoiceById();

    if (invoice === null) {
      setLoading(false);
      return;
    }

    setEditingInvoice(invoice);

    const pygDate = new Date(invoice.invoiceDate!);
    const invoiceDate = new Date(pygDate.getTime() + 3 * 60 * 60 * 1000);
    setInvoiceDate(invoiceDate);
    const stampPygDate = new Date(invoice.stampBeginDate!);
    const stampBeginDate = new Date(
      stampPygDate.getTime() + -9 * 60 * 60 * 1000
    );

    setStampDate(stampBeginDate);

    onItemsChange(invoice.items!, invoice);

    setCurrency(invoice.currency ? invoice.currency : PYG);
    setPygExchangeRate(invoice.pygExchangeRate);

    setLoading(false);
  };

  useEffect(() => {
    const newInvoiceDate = changeTZToPyg(new Date());
    if (editingInvoice !== null) {
      editingInvoice.invoiceDate = changeTZToUtc(newInvoiceDate).toISOString();
      setInvoiceDate(newInvoiceDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddItemClick = () => {
    //const newItems = [...items, newInvoiceItem()];
    //onItemsChange(newItems);
    if (editingInvoice === null) {
      return;
    }

    const newItems = [
      ...(editingInvoice.items || []),
      newReceivedInvoiceItem(),
    ];
    onItemsChange(newItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const onItemDelete = (index: number) => {
    if (editingInvoice === null) {
      return;
    }

    const newItems = [...(editingInvoice.items || [])];
    newItems.splice(index, 1);

    onItemsChange(newItems);
  };

  const onItemChange = (index: number, item: ReceivedInvoiceItem) => {
    if (editingInvoice === null) {
      return;
    }

    const newItems = [...(editingInvoice.items || [])];
    newItems[index] = item;
    onItemsChange(newItems);
  };

  const onItemsChange = (
    newItems: ReceivedInvoiceItem[],
    baseInvoice?: ReceivedInvoice | null
  ) => {
    const base = baseInvoice !== undefined ? baseInvoice : editingInvoice;

    if (base === null) {
      return;
    }

    let newTotalAmount: number | undefined = undefined;
    let newAmount10Percent: number | undefined = undefined;
    let newAmount5Percent: number | undefined = undefined;
    let newAmountExempt: number | undefined = undefined;
    let newTaxed10Percent: number | undefined = undefined;
    let newTaxed5Percent: number | undefined = undefined;
    let newIva10Percent: number | undefined = undefined;
    let newIva5Percent: number | undefined = undefined;

    for (let item of newItems) {
      if (item.unitAmount !== undefined && item.quantity !== undefined) {
        newTotalAmount = newTotalAmount
          ? newTotalAmount + item.unitAmount * item.quantity
          : item.unitAmount * item.quantity;
      }

      if (item.amount10Percent !== undefined) {
        newAmount10Percent = newAmount10Percent
          ? newAmount10Percent + item.amount10Percent
          : item.amount10Percent;
      }

      if (item.amount5Percent !== undefined) {
        newAmount5Percent = newAmount5Percent
          ? newAmount5Percent + item.amount5Percent
          : item.amount5Percent;
      }

      if (item.amountExempt !== undefined) {
        newAmountExempt = newAmountExempt
          ? newAmountExempt + item.amountExempt
          : item.amountExempt;
      }

      if (item.taxed10Percent !== undefined) {
        newTaxed10Percent = newTaxed10Percent
          ? newTaxed10Percent + item.taxed10Percent
          : item.taxed10Percent;
      }

      if (item.taxed5Percent !== undefined) {
        newTaxed5Percent = newTaxed5Percent
          ? newTaxed5Percent + item.taxed5Percent
          : item.taxed5Percent;
      }

      if (item.iva10Percent !== undefined) {
        newIva10Percent = newIva10Percent
          ? newIva10Percent + item.iva10Percent
          : item.iva10Percent;
      }

      if (item.iva5Percent !== undefined) {
        newIva5Percent = newIva5Percent
          ? newIva5Percent + item.iva5Percent
          : item.iva5Percent;
      }
    }

    setTotalAmount(newTotalAmount);
    setAmount10Percent(newAmount10Percent);
    setAmount5Percent(newAmount5Percent);
    setAmountExempt(newAmountExempt);
    setIva10Percent(newIva10Percent);
    setIva5Percent(newIva5Percent);

    setEditingInvoice({
      ...base,
      items: newItems,
      amount: newTotalAmount,
      amount10Percent: newAmount10Percent,
      amount5Percent: newAmount5Percent,
      amountExempt: newAmountExempt,
      taxed10Percent: newTaxed10Percent,
      taxed5Percent: newTaxed5Percent,
      iva10Percent: newIva10Percent,
      iva5Percent: newIva5Percent,
    });

    setItems(newItems);
  };

  const itemErrors = extractArrayErrors("items", errors);

  if (loading) {
    return <Loading />;
  }

  if (loadNotFound) {
    return <NotFound />;
  }

  if (loadError) {
    return <ServerError />;
  }

  return (
    <>
      <CCard>
        <CCardHeader>
          {editingInvoice !== null && editingInvoice.id !== undefined ? (
            <>
              {readonly ? "Ver" : "Editar"} Comprobante Recibido{" "}
              <b>#{editingInvoice.id}</b>
            </>
          ) : (
            <>Agregar Comprobante Recibido</>
          )}
        </CCardHeader>
        <CCardBody>
          <fieldset disabled={readonly}>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Proveedor:</CLabel>
                </CCol>
                <CCol md={10}>
                  <SupplierSelect
                    value={
                      selectedSupplier !== undefined ? selectedSupplier : null
                    }
                    onChange={onSupplierChange}
                  ></SupplierSelect>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Razón Social:
                  </CLabel>
                </CCol>

                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingInvoice?.supplierSocialName
                    )}
                    onChange={onSupplierSocialNameChange}
                    disabled={selectedSupplier !== undefined}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors("supplierSocialName", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> RUC emisor:
                  </CLabel>
                </CCol>

                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(editingInvoice?.supplierRuc)}
                    onChange={onSupplierRucChange}
                    disabled={selectedSupplier !== undefined}
                  ></CInput>
                  <FieldErrors
                    errors={getFieldErrors("supplierRuc", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Nombre fantasía:</CLabel>
                </CCol>

                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingInvoice?.supplierFantasyName
                    )}
                    onChange={onSupplierFantasyNameChange}
                    disabled={selectedSupplier !== undefined}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors("supplierFantasyName", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Timbrado:</CLabel>
                </CCol>

                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingInvoice?.stampIdentifier
                    )}
                    onChange={onStampIdentifierChange}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors("stampIdentifier", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel>Fecha Timbrado:</CLabel>
                </CCol>

                <CCol md={4}>
                  <Datetime
                    className="pl-0"
                    onChange={onStampBeginDateChange}
                    value={stampDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                  <FieldErrors
                    errors={
                      getFieldErrors("stampBeginDate", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Establecimiento:
                  </CLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingInvoice?.establishmentCode
                    )}
                    onChange={onEstablishmentCodeChange}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors("establishmentCode", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Punto de Expedición:
                  </CLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(
                      editingInvoice?.dispatchPointIdentifier
                    )}
                    onChange={onDispatchPointIdentifierChange}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors(
                        "dispatchPointIdentifier",
                        errors
                      ) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Nro de Factura:
                  </CLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(editingInvoice?.invoiceNumber)}
                    onChange={onInvoiceNumberChange}
                  ></CInput>
                  <FieldErrors
                    errors={getFieldErrors("invoiceNumber", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Emitido a:
                  </CLabel>
                </CCol>
                <CCol md={4}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(taxPayer.socialName)}
                    disabled
                  ></CInput>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(taxPayer.ruc)}
                    disabled
                  ></CInput>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Fecha de emisión:
                  </CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={onInvoiceDateChange}
                    value={invoiceDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                  <FieldErrors
                    errors={getFieldErrors("invoiceDate", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Condición de venta:
                  </CLabel>
                </CCol>
                <CCol md={2}>
                  <div className="form-check form-check-inline">
                    <CInputRadio
                      id="saleTypeCash"
                      name="saleType"
                      className="form-check-input"
                      value={INVOICE_SALE_TYPE_CASH}
                      defaultChecked={
                        editingInvoice !== null &&
                        editingInvoice.saleType === INVOICE_SALE_TYPE_CASH
                      }
                      onChange={() => onSaleTypeChange(INVOICE_SALE_TYPE_CASH)}
                    ></CInputRadio>
                    <CLabel htmlFor="saleTypeCash" className="form-check-label">
                      {INVOICE_SALE_TYPE_CASH_LABEL}
                    </CLabel>
                  </div>
                </CCol>
                <CCol md={2}>
                  <div className="form-check form-check-inline">
                    <CInputRadio
                      id="saleTypeCredit"
                      name="saleType"
                      className="form-check-input"
                      value={INVOICE_SALE_TYPE_CREDIT}
                      defaultChecked={
                        editingInvoice !== null &&
                        editingInvoice.saleType === INVOICE_SALE_TYPE_CREDIT
                      }
                      onChange={() =>
                        onSaleTypeChange(INVOICE_SALE_TYPE_CREDIT)
                      }
                    ></CInputRadio>
                    <CLabel
                      htmlFor="saleTypeCredit"
                      className="form-check-label"
                    >
                      {INVOICE_SALE_TYPE_CREDIT_LABEL}
                    </CLabel>
                  </div>
                </CCol>
                <FieldErrors
                  errors={getFieldErrors("saleType", errors) as string[]}
                ></FieldErrors>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Moneda:</CLabel>
                </CCol>
                <CCol md={2}>
                  <CSelect
                    type="text"
                    defaultValue={
                      editingInvoice !== null ? editingInvoice.currency : PYG
                    }
                    onChange={onCurrencyChange}
                  >
                    <option value={""} disabled>
                      -----
                    </option>
                    {Array.from(CURRENCY_CHOICES.entries()).map((entry) => {
                      return (
                        <option key={entry[0]} value={entry[0]}>
                          {entry[1]}
                        </option>
                      );
                    })}
                  </CSelect>
                  <FieldErrors
                    errors={getFieldErrors("currency", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            {currency !== PYG ? (
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>
                      <span className="text-danger">*</span> Cambio:
                    </CLabel>
                  </CCol>
                  <CCol md={2}>
                    <PYGField
                      placeholder="Total"
                      limit={1000000000000}
                      onChange={onPygExchangeRateChange}
                      value={pygExchangeRate}
                    />
                    <FieldErrors
                      errors={
                        getFieldErrors("pygExchangeRate", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
            ) : (
              <></>
            )}

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Obs:</CLabel>
                </CCol>
                <CCol md={10}>
                  <CTextarea
                    placeholder="Info Adicional"
                    defaultValue={
                      editingInvoice !== null ? editingInvoice.obs : undefined
                    }
                    onChange={onObsChange}
                  ></CTextarea>
                  <FieldErrors
                    errors={getFieldErrors("obs", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>
                    <span className="text-danger">*</span> Total:
                  </CLabel>
                </CCol>
                <CCol md={2}>
                  <CurrencyField
                    currency={currency}
                    placeholder="Total"
                    limit={1000000000000}
                    disabled={true}
                    value={totalAmount}
                  />
                  <FieldErrors
                    errors={getFieldErrors("amount", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Total 10%:</CLabel>
                </CCol>
                <CCol md={2}>
                  <CurrencyField
                    currency={currency}
                    placeholder="Total 10%"
                    limit={1000000000000}
                    disabled={true}
                    value={amount10Percent}
                  />
                  <FieldErrors
                    errors={
                      getFieldErrors("amount10Percent", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel>Total 5%:</CLabel>
                </CCol>
                <CCol md={2}>
                  <CurrencyField
                    currency={currency}
                    placeholder="Total 5%"
                    limit={1000000000000}
                    disabled={true}
                    value={amount5Percent}
                  />
                  <FieldErrors
                    errors={
                      getFieldErrors("amount5Percent", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel>Total Exento:</CLabel>
                </CCol>
                <CCol md={2}>
                  <CurrencyField
                    currency={currency}
                    placeholder="Total Exento"
                    limit={1000000000000}
                    disabled={true}
                    value={amountExempt}
                  />
                  <FieldErrors
                    errors={getFieldErrors("amountExempt", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>IVA 10%:</CLabel>
                </CCol>
                <CCol md={2}>
                  <CurrencyField
                    currency={currency}
                    placeholder="IVA 10%"
                    limit={1000000000000}
                    disabled={true}
                    value={iva10Percent}
                  />
                  <FieldErrors
                    errors={getFieldErrors("iva10Percent", errors) as string[]}
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel>IVA 5%:</CLabel>
                </CCol>
                <CCol md={2}>
                  <CurrencyField
                    currency={currency}
                    placeholder="IVA 5%"
                    limit={1000000000000}
                    disabled={true}
                    value={iva5Percent}
                  />
                  <FieldErrors
                    errors={getFieldErrors("iva5Percent", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CRow>
              <CCol>
                <h4>Ítems</h4>
              </CCol>
            </CRow>
            {items.map((item, ix) => (
              <CRow key={ix}>
                <CCol>
                  <ReceivedInvoiceItemForm
                    currency={currency}
                    item={item}
                    onDelete={() => onItemDelete(ix)}
                    onChange={(item) => onItemChange(ix, item)}
                    errors={ix < itemErrors.length ? itemErrors[ix] : {}}
                  />
                </CCol>
              </CRow>
            ))}
            <CRow>
              <CCol>
                <CButton
                  className="btn btn-primary float-right"
                  onClick={onAddItemClick}
                >
                  Agregar Ítem
                </CButton>
              </CCol>
            </CRow>
          </fieldset>
        </CCardBody>
        <CCardFooter>
          {readonly !== true ? (
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          ) : (
            <></>
          )}
        </CCardFooter>
      </CCard>
    </>
  );
};

export default ReceivedInvoiceForm;
